import cn from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { PopoverItemType } from 'types/ui'
import Checkbox from 'components/ui/checkbox'
import Icon, { IconKey } from 'components/ui/icon'
import Tooltip from 'components/ui/tooltip'
import { NestedPopover } from './NestedPopover'
type Props = {
  compact?: boolean
  item: PopoverItemType
  selected?: Pick<PopoverItemType, 'value'> | null | undefined
  onClick: (arg0: PopoverItemType) => void
  scrollToTop?: boolean
}
export const PopoverItem = ({
  compact,
  item,
  selected,
  onClick,
  scrollToTop,
}: Props): React.ReactNode => {
  const [isHover, setHover] = useState<boolean>(false)
  const handleMouseEnter = useCallback(() => setHover(true), [])
  const handleMouseLeave = useCallback(() => setHover(false), [])
  const tooltip = item.tooltip && item.tooltip.length > 0 ? item.tooltip : null
  const handleMouseOver = useCallback(() => {
    if (!isHover) {
      setHover(true)
    }
  }, [isHover])

  const renderCheckbox = (
    type: PopoverItemType['type'] | null | undefined,
    label: React.ReactNode,
    checked: PopoverItemType['checked'] | null | undefined,
    onChange: PopoverItemType['onChange'],
  ) => {
    if (!type) return

    if (type === 'checkbox') {
      return (
        <Checkbox
          label={label}
          checked={checked !== 'none'}
          partial={checked === 'some'}
          onChange={onChange}
        />
      )
    }
  }

  const {
    badge,
    checked,
    destructive,
    disabled,
    icon,
    label,
    title,
    link,
    nested,
    nodeRight,
    onChange,
    renderIcon,
    type,
    value,
    valueContainer,
  } = item
  const normalStyle = type !== 'checkbox'
  useEffect(() => {
    if (scrollToTop && selected && selected.value === value) {
      const elements = document.getElementsByClassName('popover-item--selected')

      const element = elements[0]

      if (element) {
        element.scrollIntoView({
          block: 'start',
          behavior: 'auto',
        })
      }
    }
  }, [selected, value])
  const className = cn('popover-item', {
    'popover-item--compact': compact,
    'popover-item--destructive': destructive,
    'popover-item--disabled': disabled,
    'popover-item--selected':
      item.selected || (selected && item.value === selected.value),
    'popover-item--extended': badge,
    'popover-item--nested': nested && nested.length > 0,
  })
  let Element
  let linkProps = {}

  if (type === 'link' && link) {
    Element = 'a'
    linkProps = {
      href: link.url,
      target: link.target,
      download: link.download,
      rel: link.rel,
    }
  } else if (type === 'router-link' && link) {
    Element = Link
    linkProps = {
      to: link.url,
      target: link.target,
    }
  } else {
    Element = 'div'
  }

  const ValueContainer = valueContainer || 'div'
  const titleValue = typeof label === 'string' && label ? label : title || null

  return (
    <ValueContainer className="popover-item-container">
      {item?.border && <div className="popover-item--menu-divider" />}

      <Element
        className={className}
        {...linkProps}
        title={normalStyle ? titleValue ?? undefined : undefined}
        onClick={nested || disabled ? undefined : () => onClick(item)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseOver={handleMouseOver}
      >
        <TooltipContainer tooltip={tooltip ?? undefined}>
          {renderCheckbox(type, label, checked, onChange)}
          {renderIcon ? (
            <div className="popover-item-icon">{renderIcon()}</div>
          ) : null}
          {icon && normalStyle ? (
            <Icon
              className="popover-item-icon"
              width="24"
              height="24"
              type={icon as IconKey}
            />
          ) : null}
          {normalStyle ? <span>{label}</span> : null}
          {badge && <div className="popover-item-badge">{badge}</div>}
          {nodeRight && (
            <div className="popover-item-node-right">{nodeRight}</div>
          )}
          {Array.isArray(nested) && (
            <>
              <div className="popover-item-arrow">
                <Icon type="arrow-right" />
              </div>
              {isHover && (
                <NestedPopover
                  compact={compact}
                  items={nested}
                  onClick={onClick}
                />
              )}
            </>
          )}
        </TooltipContainer>
      </Element>
    </ValueContainer>
  )
}

const TooltipContainer = ({
  children,
  tooltip,
}: {
  children: React.ReactNode
  tooltip?: string
}) => {
  return tooltip ? (
    <Tooltip position="top" content={tooltip}>
      {children}
    </Tooltip>
  ) : (
    children
  )
}
